// edgeLlmCall — Produktions-LlmCall som ringer ai-workflow-assistant
// edge-function:n. Injicerar agent.systemPrompt som första user-message
// (edge-function:n har sin egen SYSTEM_PROMPT som styrkurs men läser
// agent-specific instruktioner från början av user-turn:en).
//
// Parsear responsen för att hitta tool-calls i JSON-fences och extrahera
// en strukturerad payload. Fallback: hela responsen som text-payload.

import { supabase } from '@/integrations/supabase/client';
import { extractWorkflowJson } from '@/lib/aiJsonStrip';
import type { LlmCall, LlmAgentCallInput, LlmAgentCallResult } from '@/lib/aiAgents/llmAgentRunner';

/**
 * Bygg user-turn-prefix som instruerar modellen att röja sig som en
 * specifik agent. Edge-function:n har en bas-system-prompt; denna prefix
 * narrow:ar rollen ner till agent-nivå utan att kräva egen edge-route.
 */
function buildAgentTurnPrefix(input: LlmAgentCallInput): string {
  const allowedList = input.allowedTools.map(t => `- ${String(t.name)}`).join('\n');
  const prevText = input.previousOutput
    ? JSON.stringify(input.previousOutput, null, 2)
    : '(ingen tidigare agent-output)';
  return [
    `[AGENT: ${input.agent.id}]`,
    '',
    input.agent.systemPrompt,
    '',
    `Allowed tools (får bara kalla dessa):`,
    allowedList,
    '',
    `Previous agent output:`,
    '```json',
    prevText,
    '```',
    '',
    `User prompt:`,
    input.userPrompt,
  ].join('\n');
}

/**
 * Skapa en LlmCall som pratar med edge-function:n ai-workflow-assistant.
 * Kallas av useDiagramPipeline i produktion.
 */
export function createEdgeLlmCall(opts: {
  /** Edge-function URL. Default läses från VITE_SUPABASE_URL. */
  endpoint?: string;
  /** Auth-header-factory. Default använder supabase.auth.getSession. */
  getAuthToken?: () => Promise<string | null>;
  /** Abort-signal för cancel(). */
  signal?: AbortSignal;
} = {}): LlmCall {
  const getToken = opts.getAuthToken ?? (async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  });

  return async function edgeLlmCall(input: LlmAgentCallInput): Promise<LlmAgentCallResult> {
    const base = opts.endpoint
      ?? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-workflow-assistant`;
    const token = await getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    };

    const agentTurn = buildAgentTurnPrefix(input);
    const body = {
      messages: [
        { role: 'user', content: agentTurn },
      ],
      intent: 'build_new',
    };

    try {
      const resp = await fetch(base, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: opts.signal,
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        return {
          ok: false, payload: null, cost: 0, toolCalls: [],
          error: `edge-${resp.status}: ${errText.slice(0, 200)}`,
        };
      }

      const text = await resp.text();
      const workflowJsons = extractWorkflowJson(text);
      const payload = workflowJsons.length > 0
        ? safeParse(workflowJsons[0])
        : { text };

      // Y3 + AA5: token-usage från response-header om edge-function
      // exponerar det (AA5 forwarder från Lovable gateway). Headers att
      // leta efter: x-token-usage, openai-usage, x-usage*. Om stream:
      // AA5 lägger usage i sista SSE-chunkens data-fält via
      // stream_options.include_usage=true.
      const tokenHeader =
        resp.headers.get('x-token-usage') ??
        resp.headers.get('openai-usage') ??
        resp.headers.get('x-usage');
      const sseUsage = extractUsageFromSse(text);
      const headerCost = parseUsageHeader(tokenHeader) ?? sseUsage;
      // Extrahera tool-calls ur strukturerad payload om den innehåller
      // "tools"-array (för framtida agent-self-reporting).
      const toolCalls: LlmAgentCallResult['toolCalls'] = [];
      if (payload && typeof payload === 'object' && 'tools' in payload) {
        const tools = (payload as { tools?: unknown }).tools;
        if (Array.isArray(tools)) {
          for (const t of tools) {
            if (t && typeof t === 'object' && 'name' in t) {
              toolCalls.push({
                tool: String((t as { name: unknown }).name),
                args: ((t as { args?: Record<string, unknown> }).args) ?? {},
              });
            }
          }
        }
      }

      // Y3: föredra server-rapporterad token-usage; fallback till längd-
      // baserad approximation när header saknas (edge-function ännu inte
      // pushar usage-info). aiTokenMeter är separat audit-väg.
      const cost = headerCost ?? Math.ceil(text.length / 200);

      return {
        ok: true,
        payload,
        cost,
        toolCalls,
      };
    } catch (e) {
      const err = e as Error;
      return {
        ok: false, payload: null, cost: 0, toolCalls: [],
        error: err.name === 'AbortError' ? 'cancelled' : (err.message ?? 'network-error'),
      };
    }
  };
}

function safeParse(jsonStr: string): unknown {
  try { return JSON.parse(jsonStr); } catch { return { text: jsonStr }; }
}

/**
 * AA5: extrahera `usage`-objekt från sista SSE-`data:` i en stream-
 * respons. Lovable gateway skickar `{ "usage": { "prompt_tokens": N,
 * "completion_tokens": M, "total_tokens": K } }` som näst-sista / sista
 * SSE-chunk när `stream_options: { include_usage: true }` är satt.
 *
 * Returnerar credit-cost (tokens/100) eller null om ingen usage-info.
 */
export function extractUsageFromSse(text: string): number | null {
  // Leta bakifrån eftersom usage-chunken är sist.
  const lines = text.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line.startsWith('data:')) continue;
    const payload = line.slice(5).trim();
    if (!payload || payload === '[DONE]') continue;
    try {
      const obj = JSON.parse(payload) as Record<string, unknown>;
      const usage = obj.usage as
        | { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number }
        | undefined;
      if (usage) {
        const total = usage.total_tokens
          ?? (usage.prompt_tokens ?? 0) + (usage.completion_tokens ?? 0);
        if (total > 0) return Math.ceil(total / 100);
      }
    } catch {
      // Ogiltig JSON i data: — hoppa över, försök nästa rad bakåt.
      continue;
    }
  }
  return null;
}

/**
 * Y3: Parsar usage-header i flera format:
 * - "input=150,output=300" → 450
 * - "150,300" → 450
 * - JSON: {"prompt_tokens":150,"completion_tokens":300} → 450
 * - "0.0031" (dollars) → 0 (ignoreras — vi räknar i tokens/credits)
 * Null om headern saknas eller är ogenomtänglig.
 */
export function parseUsageHeader(raw: string | null): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // JSON-shape
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed) as Record<string, unknown>;
      const prompt = Number(obj.prompt_tokens ?? obj.input_tokens ?? obj.input ?? 0);
      const completion = Number(obj.completion_tokens ?? obj.output_tokens ?? obj.output ?? 0);
      const sum = prompt + completion;
      return sum > 0 ? Math.ceil(sum / 100) : null; // 100 tokens ~ 1 credit ballpark
    } catch {
      return null;
    }
  }
  // key=value,key=value
  if (trimmed.includes('=')) {
    const parts = Object.fromEntries(
      trimmed.split(',').map((p) => p.split('=').map((s) => s.trim())),
    );
    const input = Number(parts.input ?? parts.prompt ?? 0);
    const output = Number(parts.output ?? parts.completion ?? 0);
    const sum = input + output;
    return sum > 0 ? Math.ceil(sum / 100) : null;
  }
  // "n1,n2" — rå komma-separerad
  const nums = trimmed.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n));
  if (nums.length >= 2) {
    const sum = nums[0] + nums[1];
    return sum > 0 ? Math.ceil(sum / 100) : null;
  }
  // Enkel number
  const single = Number(trimmed);
  if (!isNaN(single) && single > 10) return Math.ceil(single / 100);
  return null;
}
